// apps/client/src/components/AIChat/InsightCard.tsx

import React from 'react';
import {
  TrendingUp,
  AlertTriangle,
  Info,
  Lightbulb,
  AlertCircle,
  BarChart3,
  LucideProps,
} from 'lucide-react';
import { Insight } from './types';

interface InsightCardProps {
  insight: Insight;
}

// Icon map with proper typing for React 19 compatibility
const iconMap: Record<string, React.FC<LucideProps>> = {
  trend: TrendingUp as React.FC<LucideProps>,
  anomaly: AlertCircle as React.FC<LucideProps>,
  comparison: BarChart3 as React.FC<LucideProps>,
  recommendation: Lightbulb as React.FC<LucideProps>,
  warning: AlertTriangle as React.FC<LucideProps>,
};

/**
 * Display AI-generated insights with appropriate styling and icons
 */
export function InsightCard({ insight }: InsightCardProps) {
  const { type, title, description, severity = 'info' } = insight;

  // Select icon based on insight type
  const Icon = iconMap[type] || (Info as React.FC<LucideProps>);

  // Select colors based on severity (dark theme)
  const colors = {
    info: 'bg-blue-900/30 border-blue-500/50 text-blue-200',
    warning: 'bg-yellow-900/30 border-yellow-500/50 text-yellow-200',
    critical: 'bg-red-900/30 border-red-500/50 text-red-200',
  }[severity];

  const iconColors = {
    info: 'text-blue-400',
    warning: 'text-yellow-400',
    critical: 'text-red-400',
  }[severity];

  return (
    <div className={`rounded-md border p-2 text-sm ${colors}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColors}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-xs">{title}</p>
          <p className="text-xs mt-0.5 opacity-90">{description}</p>
        </div>
      </div>
    </div>
  );
}
